// /onboard/agreement: the firm reviews its agreement PDF and signs it inside its onboarding session.
// Flow and copy follow the Upstream Portal's SignDocument.tsx: the document, the disclosure, a typed full legal
// name, an explicit consent tick, Sign; or Decline with a reason. Phones get "Open the agreement" instead of the
// inline frame (mobile browsers don't render PDFs in frames reliably).
import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Download, ExternalLink, FileText, XCircle } from 'lucide-react';
import type { DeclineAgreementRequest, FirmAgreementResponse, FirmAgreementView, SignAgreementRequest } from '@upm/domain';
import { api, ApiError } from '../lib/api';
import { firstName, fmtDate, fmtDateTime } from '../lib/format';
import { Banner, Button, ButtonA, ButtonLink, Spinner, TextArea } from '../ui';
import { FirmHeader, FirmPage } from '../firm/FirmHeader';
import { FirmMessage } from '../firm/Message';
import { NoSession } from '../firm/Entry';
import { SignatureForm, type SignatureValue } from './SignatureForm';

/** The firm's copy of the PDF, streamed through the API under its session cookie. */
export const firmDocUrl = (id: string, kind: 'original' | 'final' = 'original', inline = false) =>
  `/api/intake/agreement/${id}/document?kind=${kind}${inline ? '&disposition=inline' : ''}`;

export const fmtSize = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

const errorText = (e: unknown) => (e instanceof ApiError && e.code !== 'internal' && e.code !== 'network' ? e.message : 'That didn’t go through. Check your connection and try again.');

export function SignAgreementPage() {
  const [data, setData] = useState<FirmAgreementResponse | null>(null);
  const [state, setState] = useState<'loading' | 'nosession' | 'error' | 'ok'>('loading');
  const load = useCallback(async () => {
    try { setData(await api.get<FirmAgreementResponse>('/api/intake/agreement')); setState('ok'); }
    catch (e) { setState(e instanceof ApiError && e.status === 401 ? 'nosession' : 'error'); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // The signed copy is scanned before it can be downloaded; check back until it is ready.
  const preparing = data?.agreement?.signedCopy === 'preparing';
  useEffect(() => {
    if (!preparing) return;
    const t = setInterval(() => { void load(); }, 5000);
    return () => clearInterval(t);
  }, [preparing, load]);

  if (state === 'nosession') return <NoSession />;
  if (state === 'error') return <FirmMessage title="Something went wrong"><p className="m-0">We couldn’t load your agreement. Refresh the page to try again.</p></FirmMessage>;
  if (!data) return <div className="min-h-screen bg-page"><Spinner label="Loading your agreement" /></div>;
  const a = data.agreement;

  return (
    <FirmPage>
      <FirmHeader subtitle={data.firmName} lead={data.lead} />
      <main className="mx-auto flex max-w-[880px] flex-col gap-5 px-4 pb-16 pt-6 sm:px-8 sm:pt-8">
        <div className="flex flex-col gap-2">
          <span className="eyebrow">Plan · Sign the agreement</span>
          <h1 className="m-0 text-[26px] font-bold leading-[1.2] tracking-[-0.02em] sm:text-[30px]">Your Upstream Portal agreement</h1>
        </div>
        {!a && (
          <Banner tone="info">There’s no agreement to sign yet. {firstName(data.lead.name)} will email you when it’s ready.</Banner>
        )}
        {a && <AgreementBody a={a} leadName={data.lead.name} onChange={(next) => setData({ ...data, agreement: next })} />}
        <div><ButtonLink to="/onboard/status" variant="link" className="!px-0">Back to your status page</ButtonLink></div>
      </main>
    </FirmPage>
  );
}

function DocumentCard({ a, inline }: { a: FirmAgreementView; inline: boolean }) {
  return (
    <section aria-label="Agreement document" className="flex flex-col gap-3 rounded-2xl border border-line bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <FileText aria-hidden="true" size={28} className="flex-none text-brand" />
          <div className="min-w-0">
            <div className="font-semibold [overflow-wrap:anywhere]">{a.fileName}</div>
            <div className="text-[13px] text-muted">{a.pageCount} {a.pageCount === 1 ? 'page' : 'pages'} · {fmtSize(a.sizeBytes)}{a.sentAt ? ` · Sent ${fmtDate(a.sentAt)}` : ''}</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ButtonA href={firmDocUrl(a.id, 'original', true)} target="_blank" rel="noopener" variant="secondary" size="sm"><ExternalLink aria-hidden="true" size={16} />Open the agreement</ButtonA>
          <ButtonA href={firmDocUrl(a.id)} download variant="link" size="sm"><Download aria-hidden="true" size={16} />Download</ButtonA>
        </div>
      </div>
      {inline && (
        <iframe
          title={`${a.fileName} (PDF)`}
          src={`${firmDocUrl(a.id, 'original', true)}#view=FitH&navpanes=0`}
          className="hidden h-[70vh] min-h-[480px] w-full rounded-xl border border-line bg-panel sm:block"
        />
      )}
      {inline && <p className="m-0 text-[13px] text-muted sm:hidden">Open the agreement to read it full screen, then come back here to sign.</p>}
    </section>
  );
}

function AgreementBody({ a, leadName, onChange }: { a: FirmAgreementView; leadName: string; onChange: (a: FirmAgreementView) => void }) {
  const [mode, setMode] = useState<'sign' | 'decline'>('sign');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const lead = firstName(leadName);

  const sign = async (v: SignatureValue) => {
    setBusy(true);
    setError(null);
    try { onChange(await api.post<FirmAgreementView>('/api/intake/agreement/sign', { agreementId: a.id, documentSha256: a.sha256, ...v } satisfies SignAgreementRequest)); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  const decline = async () => {
    setBusy(true);
    setError(null);
    try { onChange(await api.post<FirmAgreementView>('/api/intake/agreement/decline', { agreementId: a.id, reason: reason.trim() } satisfies DeclineAgreementRequest)); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };

  if (a.status === 'firm_signed' || a.status === 'executed') {
    const done = a.status === 'executed';
    return (
      <>
        <section role="status" className="flex flex-col items-start gap-3 rounded-2xl border border-line bg-white p-5 sm:p-7">
          <span aria-hidden="true" className="flex h-12 w-12 items-center justify-center rounded-full bg-brand text-white"><CheckCircle2 size={26} /></span>
          <h2 className="m-0 text-[22px] font-bold tracking-[-0.01em]">{done ? 'Signed by both sides' : 'Thank you. You’ve signed.'}</h2>
          <p className="m-0 text-ink-2">
            {a.signer.signedName} signed{a.signer.signedTitle ? ` as ${a.signer.signedTitle}` : ''}{a.signer.signedAt ? ` on ${fmtDateTime(a.signer.signedAt)}` : ''}.
            {' '}{done
              ? `${a.countersigner.name} countersigned for Vertekal${a.countersigner.signedAt ? ` on ${fmtDate(a.countersigner.signedAt)}` : ''}.`
              : `Waiting for Vertekal to countersign. ${a.countersigner.name} signs next, and we’ll email you the fully signed copy.`}
          </p>
          {done && (a.signedCopy === 'ready'
            ? <ButtonA href={firmDocUrl(a.id, 'final')} download variant="primary" size="lg"><Download aria-hidden="true" size={18} />Download the signed agreement</ButtonA>
            : <p className="m-0 text-sm text-muted">Preparing your signed copy. This takes a minute.</p>)}
        </section>
        <DocumentCard a={a} inline={false} />
      </>
    );
  }
  if (a.status === 'declined') {
    return (
      <section role="status" className="flex flex-col items-start gap-3 rounded-2xl border border-line bg-white p-5 sm:p-7">
        <XCircle aria-hidden="true" size={40} className="text-bad" />
        <h2 className="m-0 text-[22px] font-bold tracking-[-0.01em]">You declined this agreement</h2>
        <p className="m-0 text-ink-2">{lead} has your note and will be in touch{a.signer.declinedAt ? ` (declined ${fmtDate(a.signer.declinedAt)})` : ''}. Questions? Use “Questions? {leadName}” at the top of the page.</p>
      </section>
    );
  }

  return (
    <>
      <DocumentCard a={a} inline />
      <section aria-labelledby="sign-h" className="flex flex-col gap-4 rounded-2xl border border-line bg-white p-5 sm:p-7">
        <h2 id="sign-h" className="m-0 text-xl font-bold tracking-[-0.01em]">{mode === 'sign' ? 'Sign the agreement' : 'Decline the agreement'}</h2>
        {mode === 'sign' ? (
          <SignatureForm
            defaultName={a.signer.name}
            busy={busy}
            error={error}
            onSign={(v) => void sign(v)}
            secondary={<Button variant="quiet" onClick={() => { setError(null); setMode('decline'); }}>Decline</Button>}
          />
        ) : (
          <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (reason.trim()) void decline(); }}>
            <TextArea label="Why are you declining?" hint={`${lead} reads this and follows up with you.`} rows={4} value={reason} onChange={(e) => setReason(e.target.value)} />
            {error && <Banner tone="error" role="alert">{error}</Banner>}
            <div className="flex flex-wrap-reverse items-center justify-between gap-3">
              <Button variant="quiet" onClick={() => { setError(null); setMode('sign'); }}>Back</Button>
              <Button type="submit" variant="danger" size="lg" disabled={busy || !reason.trim()}>{busy ? 'Sending…' : 'Decline the agreement'}</Button>
            </div>
          </form>
        )}
      </section>
      <p className="m-0 text-center text-xs text-muted">Secured by Upstream · Agreement {a.id.slice(0, 8)} · SHA-256 {a.sha256.slice(0, 12)}…</p>
    </>
  );
}
